import React, {useState, useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Flex,
    Heading,
    IconButton,
    Skeleton,
    Text,
    VStack,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Tooltip} from '@chakra-ui/react'
import {InfoCircle} from '../../icons'
import {useBazarVoice} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'
import {usePromotions} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import DisplayPrice from '../../display-price'
import StarRating from '../../pdp-reviews/partials/star-rating'
import {RESOURCE_BADGE_MAPPING, normalizeResourceBadgeKey} from '../../../constants'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useBrand} from '../../../contexts/brand-context'
import {useGlobalBadges} from '../../../contexts/global-elements-context'
import {resolveBadgeCmsStyles} from '../../../utils/global-elements-utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

const Reviews = ({product, onReviewsClick}) => {
    const styles = useMultiStyleConfig('ProductView')
    const pid = product?.id
    const {isLoading, reviews: initialReviews, reviewsError} = useBazarVoice({pid})
    const [noReviews, setNoReviews] = useState(true)
    const [reviewsDataPid, setReviewsDataPid] = useState(null)
    const reviewsContainerRef = useRef(null)
    const [reservedHeight, setReservedHeight] = useState(0)
    const averageRating =
        initialReviews?.Results?.length > 0
            ? initialReviews.Results.reduce((sum, review) => sum + review.Rating, 0) /
              initialReviews.Results.length
            : null
    const totalReviews = initialReviews?.TotalResults || 0

    useEffect(() => {
        if (initialReviews?.Results) {
            setNoReviews(initialReviews.Results.length === 0)
            setReviewsDataPid(pid)
        }
    }, [initialReviews])

    const isVariantDataPending = reviewsDataPid !== pid
    const shouldRenderReviews =
        !isVariantDataPending && !((!averageRating && totalReviews === 0) || noReviews)

    useEffect(() => {
        if (!shouldRenderReviews || !reviewsContainerRef.current) return

        const nextHeight = reviewsContainerRef.current.getBoundingClientRect().height
        if (nextHeight > 0) {
            setReservedHeight(nextHeight)
        }
    }, [shouldRenderReviews, averageRating, totalReviews, isLoading, pid])

    if (reviewsError) {
        console.error('[Reviews]:', reviewsError)
        return null
    }

    return (
        <Box
            ref={reviewsContainerRef}
            minHeight={reservedHeight ? `${reservedHeight}px` : undefined}
            data-testid="reviews-container"
        >
            {shouldRenderReviews && (
                <Skeleton isLoaded={!isLoading} lineHeight={'16px'}>
                    <Flex
                        as={'a'}
                        href={'#pdp-reviews'}
                        {...styles.reviewsFlex}
                        role="button"
                        tabIndex={0}
                        aria-label="Star rating"
                        onClick={(e) => {
                            if (onReviewsClick) {
                                e.preventDefault()
                                onReviewsClick(e)
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && onReviewsClick) {
                                e.preventDefault()
                                onReviewsClick(e)
                            }
                        }}
                    >
                        {averageRating && (
                            <StarRating
                                initialRating={Number(averageRating)}
                                lock={true}
                                styles={{starWidth: '12', starHeight: '12'}}
                            />
                        )}

                        {/* Total Reviews */}
                        {totalReviews > 0 && (
                            <Text {...styles.reviewsCountText}>({totalReviews})</Text>
                        )}
                    </Flex>
                </Skeleton>
            )}
        </Box>
    )
}

Reviews.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string,
        c_bvAverageRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        c_bvReviewCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    onReviewsClick: PropTypes.func
}

const ProductViewHeader = ({
    name,
    currency,
    priceData,
    product,
    isProductPartOfBundle,
    onReviewsClick,
    isGiftPage = false,
    isLoading = false
}) => {
    const styles = useMultiStyleConfig('ProductView', {isGiftPage})
    const tileStyles = useMultiStyleConfig('ProductTile')
    const intl = useIntl()
    const config = getConfig()
    const {isOpen, onOpen, onToggle, onClose} = useDisclosure()
    const {isDynamite} = useBrand()
    const {data: globalBadges} = useGlobalBadges()
    const {site} = useMultiSite()
    const siteId = site?.id

    const promotionId = useMemo(
        () => product?.productPromotions?.[0]?.promotionId,
        [product?.productPromotions]
    )

    const calloutMsg = useMemo(
        () => product?.productPromotions?.[0]?.calloutMsg,
        [product?.productPromotions]
    )

    const promotionsQuery = usePromotions(
        {
            parameters: {ids: promotionId}
        },
        {
            enabled: !!promotionId
        }
    )

    const promotionData = promotionsQuery?.data?.data?.[0] || {}

    const TAGS_COUNT_PDP = getSiteEnvVarValue(config.app, 'TAGS_COUNT_PDP', siteId)
    const BV_ENABLE_RR_C2022 = getSiteEnvVarValue(config.app, 'BV_ENABLE_RR_C2022', siteId) || false

    const renderProductCallout = () => {
        const maxTags = TAGS_COUNT_PDP ? parseInt(TAGS_COUNT_PDP, 10) : 2
        const filteredBadges = (product?.c_textBadges || [])
            .filter((badge) => Boolean(RESOURCE_BADGE_MAPPING[normalizeResourceBadgeKey(badge)]?.id))
            .sort((a, b) => (a === 'finalsale' ? -1 : b === 'finalsale' ? 1 : 0))
            .slice(0, maxTags)

        if (!filteredBadges.length) return null

        return (
            <Box {...styles.productCallout}>
                {filteredBadges.map((badge) => {
                    const badgeMessage = RESOURCE_BADGE_MAPPING[normalizeResourceBadgeKey(badge)]

                    const badgeConfig = globalBadges?.find((b) => b.id === badge)
                    const {textStyles = [], borderRadius, opacity, text} = badgeConfig || {}

                    const {containerOverrides: cmsWrapperOverrides, badgeOverrides: cmsTextOverrides, hideStyles} =
                        resolveBadgeCmsStyles(textStyles, borderRadius, opacity, badgeConfig?.hideBlockOn)

                    if (hideStyles.doNotLoadComponent) return null

                    return (
                        <Box key={badge} {...cmsWrapperOverrides} {...(hideStyles.sx && {sx: hideStyles.sx})}>
                            <Text as="h2" {...styles.productCalloutText} {...cmsTextOverrides}>
                                {text || intl.formatMessage(badgeMessage)}
                            </Text>
                        </Box>
                    )
                })}
            </Box>
        )
    }

    const renderCalloutTooltip = () => {
        if (!promotionData?.c_showCalloutMessage || !calloutMsg) return null

        return (
            <Tooltip
                isOpen={isOpen}
                label={calloutMsg}
                {...tileStyles.toolTip}
                {...styles.calloutTooltip}
                aria-label="calloutMsg"
                placement="right"
                hasArrow
                arrowShadowColor="gray.50"
            >
                <IconButton
                    onPointerEnter={(event) => {
                        if (event.pointerType === 'mouse') {
                            onOpen()
                        }
                    }}
                    onPointerLeave={(event) => {
                        if (event.pointerType === 'mouse') {
                            onClose()
                        }
                    }}
                    onFocus={onOpen}
                    onBlur={onClose}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onToggle()
                    }}
                    {...styles.calloutTooltipButton}
                    icon={<InfoCircle {...tileStyles.toolTipIcon} />}
                    aria-label="Tooltip"
                />
            </Tooltip>
        )
    }

    const renderPriceSection = () => {
        if (isProductPartOfBundle) return null
        if (!priceData?.currentPrice && !isLoading) return null

        return (
            <Skeleton isLoaded={!!priceData?.currentPrice && !isLoading}>
                <Flex alignItems="center">
                    {priceData?.currentPrice && (
                        <DisplayPrice
                            currentPriceProps={{
                                ...styles.currentPrice,
                                color: priceData?.isOnSale
                                    ? 'webTypo.textSale'
                                    : 'webTypo.textPrimary'
                            }}
                            listPriceProps={{
                                ...styles.listPrice,
                                textDecoration: priceData?.isOnSale ? 'line-through' : 'none'
                            }}
                            priceSetProps={{
                                ...styles.setPrice
                            }}
                            containerProps={{
                                ...styles.priceContainer
                            }}
                            priceData={priceData}
                            currency={currency}
                            isPDP={true}
                        />
                    )}
                    {renderCalloutTooltip()}
                </Flex>
            </Skeleton>
        )
    }

    const renderPromotionBadge = () => {
        if (!promotionData.c_showCalloutMessage || !promotionData.name) return null

        return (
            <Text
                {...styles.promotionBadge}
                color={
                    priceData?.isOnSale
                        ? isDynamite
                            ? 'webTypo.textPrimary'
                            : 'webTypo.textSecondary'
                        : 'webTypo.textSale'
                }
            >
                {promotionData.name?.toLowerCase()}
            </Text>
        )
    }

    return (
        <VStack {...styles.headerStack}>
            <Flex justifyContent={'space-between'} alignItems={'flex-start'} gap={4} w={'100%'}>
                <Skeleton isLoaded={!!name && !isLoading}>
                    <Text as="h1" {...styles.productName} data-testid="product-name">
                        {name}
                    </Text>
                </Skeleton>

                <Box display={{base: 'block', lg: 'none'}}>
                    {BV_ENABLE_RR_C2022 === 'true' && (
                        <Reviews product={product} onReviewsClick={onReviewsClick} />
                    )}
                </Box>
            </Flex>

            {!isGiftPage && renderPriceSection()}

            {renderPromotionBadge()}
            {renderProductCallout()}

            <Box display={{base: 'none', lg: 'block'}}>
                {BV_ENABLE_RR_C2022 === 'true' && ( 
                    <Reviews product={product} onReviewsClick={onReviewsClick} />
                )}
            </Box>
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    onReviewsClick: PropTypes.func,
    isGiftPage: PropTypes.bool,
    isLoading: PropTypes.bool
}

export {Reviews}
export default ProductViewHeader
